import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Button, TextField, Alert, IconButton, Tooltip, Stack, Chip, Paper, InputBase, InputAdornment, Menu, MenuItem, useMediaQuery, useTheme, alpha } from "@mui/material";
import { AppDialog, ConfirmDialog } from "../components/dialog";
import AddRounded from '@mui/icons-material/AddRounded';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useNavigate } from "react-router-dom";
import { formatRelativeTime, formatDate } from '../utils/date';
import { buildApiUrl } from "../config";
import { buildLoginUrl } from "../utils/loginRedirect";
import { getChartPalette } from '../theme/charts';
import { ModeContext } from '../theme/ThemeProvider';

type Dashboard = {
  dashboard_id: string;
  name: string;
  description?: string | null;
  created_at: string;
  updated_at: string;
  widget_count?: number;
  last_widget_update?: string | null;
  /** 'agent' = Copilot widget grid; 'manual' = spec-driven board. */
  dashboard_type?: string | null;
  spec_key?: string | null;
  /** Which page draws a spec-driven board: `cohort` or `lookup`. */
  board_kind?: 'cohort' | 'lookup' | 'planner' | null;
};

export default function DashboardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { branding } = React.useContext(ModeContext);
  const chartPalette = React.useMemo(() => getChartPalette(branding), [branding]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editDash, setEditDash] = useState<Dashboard | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editErr, setEditErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delDash, setDelDash] = useState<Dashboard | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<'name' | 'updated' | 'created'>('updated');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);

  // Filtered and sorted dashboards
  const filteredAndSortedDashboards = useMemo(() => {
    let filtered = dashboards;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = dashboards.filter(d => 
        d.name.toLowerCase().includes(query) || 
        (d.description && d.description.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'updated':
        default:
          const timeA = new Date(a.last_widget_update || a.updated_at).getTime();
          const timeB = new Date(b.last_widget_update || b.updated_at).getTime();
          return timeB - timeA;
      }
    });
  }, [dashboards, searchQuery, sortBy]);

  useEffect(() => {
    // Preserve the deep link the user was trying to visit so they land back
    // here after sign-in (shared dashboard URLs etc.).
    const loginUrl = () =>
      buildLoginUrl('/login', window.location.pathname + window.location.search);
    const jwt = window.localStorage.getItem("klaris_jwt");
    if (!jwt) { navigate(loginUrl(), { replace: true }); return; }
    const tStr = window.localStorage.getItem('klaris_tenant');
    if (!tStr) { navigate(loginUrl(), { replace: true }); return; }
    try { setTenantId(JSON.parse(tStr).tenant_id); } catch { navigate(loginUrl(), { replace: true }); }
  }, [navigate]);

  useEffect(() => { if (tenantId) fetchDashboards(); }, [tenantId]);
  // Listen for global "dashboard:new" event raised from DashboardLayout header button
  useEffect(() => {
    const onNew = () => setCreateOpen(true);
    window.addEventListener('dashboard:new', onNew as any);
    return () => window.removeEventListener('dashboard:new', onNew as any);
  }, []);

  async function fetchDashboards() {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards`), {
        headers: { Authorization: `Bearer ${token}` }
      } as RequestInit);
      if (!resp.ok) throw new Error('Failed to fetch dashboards');
      const data = await resp.json();
      const dashboardsList = Array.isArray(data?.dashboards) ? data.dashboards : [];
      
      // Backend now returns widget_count and last_widget_update directly - no need for N+1 queries!
      setDashboards(dashboardsList);
    } catch (e: any) { setError(e?.message || 'Failed to fetch dashboards'); }
    finally { setLoading(false); }
  }

  async function handleCreate() {
    setCreateErr(null);
    if (!tenantId) return;
    const name = createName.trim();
    if (!name) { setCreateErr('Name is required'); return; }
    setCreating(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards`), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, description: createDesc.trim() || undefined })
      } as RequestInit);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || data?.error || 'Failed to create');
      setCreateOpen(false); setCreateName(""); setCreateDesc("");
      fetchDashboards();
    } catch (e: any) { setCreateErr(e?.message || 'Failed to create dashboard'); }
    finally { setCreating(false); }
  }

  async function handleSave() {
    setEditErr(null);
    if (!tenantId || !editDash) return;
    const name = editName.trim();
    setSaving(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(editDash.dashboard_id)}`), {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name || undefined, description: editDesc.trim() })
      } as RequestInit);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || data?.error || 'Failed to update');
      setEditOpen(false); setEditDash(null);
      fetchDashboards();
    } catch (e: any) { setEditErr(e?.message || 'Failed to update'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!tenantId || !delDash) return;
    setDeleting(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(delDash.dashboard_id)}`), {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      } as RequestInit);
      if (!resp.ok) throw new Error('Failed to delete');
      setDeleteOpen(false); setDelDash(null);
      fetchDashboards();
    } catch (e: any) { setError(e?.message || 'Failed to delete'); }
    finally { setDeleting(false); }
  }

  // Prefer consistent local datetime formatting across the app

  // ── Card hero "preview" ──────────────────────────────────────────────────
  // We don't have real dashboard thumbnails (would require backend rendering),
  // so we generate a stylized 4×3 mosaic that scales with widget_count.
  // This gives every card a unique visual signature: a 2-widget dashboard
  // looks meaningfully different from a 12-widget one at a glance.
  const PREVIEW_CELLS = 12;

  const renderHeroPreview = (widgetCount: number, accent: string, isManual = false) => {
    const filled = Math.min(widgetCount, PREVIEW_CELLS);
    const overflow = Math.max(0, widgetCount - PREVIEW_CELLS);

    return (
      <Box
        sx={(theme) => ({
          position: 'relative',
          height: 76,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Accent-tinted gradient — gives each card identity using its
          // chart-palette color instead of every tile being identical gray.
          background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: `1px solid ${theme.tokens.border.subtle}`,
          overflow: 'hidden',
          // Faint dot grid — adds enterprise texture without being loud.
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(${alpha(theme.tokens.text.tertiary, 0.18)} 1px, transparent 1px)`,
            backgroundSize: '10px 10px',
            opacity: 0.5,
            pointerEvents: 'none',
          },
        })}
      >
        {isManual ? (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-end',
              gap: '5px',
              height: 44,
            }}
          >
            {/* A small bar chart: this board is a fixed report, not a
                collection of pinned widgets. */}
            {[0.45, 0.7, 0.55, 0.9, 0.75, 1].map((h, i) => (
              <Box
                key={i}
                sx={{
                  width: 11,
                  height: `${h * 100}%`,
                  borderRadius: '2px 2px 0 0',
                  bgcolor: alpha(accent, 0.3 + i * 0.1),
                }}
              />
            ))}
          </Box>
        ) : widgetCount === 0 ? (
          <Typography
            sx={(theme) => ({
              position: 'relative',
              fontSize: '0.6875rem',
              fontWeight: 500,
              color: theme.tokens.text.tertiary,
              letterSpacing: '0.02em',
            })}
          >
            Empty dashboard
          </Typography>
        ) : (
          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gridTemplateRows: 'repeat(3, 1fr)',
              gap: '4px',
              width: 104,
              height: 52,
            }}
          >
            {Array.from({ length: PREVIEW_CELLS }).map((_, i) => {
              const isFilled = i < filled;
              const isOverflowSlot = overflow > 0 && i === PREVIEW_CELLS - 1;
              return (
                <Box
                  key={i}
                  sx={(theme) => ({
                    borderRadius: '2px',
                    bgcolor: isFilled
                      ? alpha(accent, 0.65)
                      : alpha(theme.tokens.text.tertiary, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.5rem',
                    fontWeight: 700,
                    color: '#fff',
                    lineHeight: 1,
                  })}
                >
                  {isOverflowSlot ? `+${overflow}` : ''}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const renderDashboardCard = (d: Dashboard, idx: number) => {
    // Brand-derived accent that varies subtly per card without becoming
    // a rainbow — Linear/Hex pattern. Falls back to brand primary.
    const accent = chartPalette[idx % chartPalette.length];
    const widgetCount = d.widget_count || 0;
    const lastTouchedAt = d.last_widget_update || d.updated_at;

    return (
      <Paper
        key={d.dashboard_id}
        variant="outlined"
        sx={(theme) => ({
          borderRadius: `${theme.ds.radius.lg}px`,
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderColor: theme.tokens.border.subtle,
          bgcolor: theme.tokens.surface.raised,
          transition: `border-color ${theme.ds.motion.duration.normal}ms ${theme.ds.motion.easing.standard}, box-shadow ${theme.ds.motion.duration.normal}ms ${theme.ds.motion.easing.standard}, transform ${theme.ds.motion.duration.normal}ms ${theme.ds.motion.easing.standard}`,
          '&:hover': {
            borderColor: theme.tokens.border.strong,
            boxShadow: theme.tokens.shadow.md,
            transform: 'translateY(-1px)',
            '& .dash-card-actions': { opacity: 1 },
          },
          '&:focus-within': {
            borderColor: theme.tokens.brand.primary,
            boxShadow: theme.tokens.shadow.focusRing,
          },
        })}
        onClick={() => navigate(
          `/dashboard/${encodeURIComponent(d.dashboard_id)}`,
          {
            state: {
              dashboardName: d.name,
              dashboardDescription: d.description,
              dashboardId: d.dashboard_id,
              // Lets the route pick a renderer without re-fetching metadata.
              dashboardType: d.dashboard_type ?? 'agent',
              boardKind: d.board_kind ?? null,
            },
          },
        )}
      >
        {/* Hero preview band — gives each card visual identity */}
        <Box sx={{ position: 'relative' }}>
          {renderHeroPreview(widgetCount, accent, d.dashboard_type === 'manual')}

          {/* Edit/Delete overlay on hover — sits on top of the hero band so
              it never crowds the title row. Translucent backdrop keeps icons
              legible against any accent color. */}
          <Box
            className="dash-card-actions"
            sx={(theme) => ({
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 0.25,
              padding: '2px',
              borderRadius: `${theme.ds.radius.sm}px`,
              bgcolor: alpha(theme.tokens.surface.raised, 0.92),
              backdropFilter: 'blur(4px)',
              boxShadow: theme.tokens.shadow.sm,
              opacity: { xs: 1, md: 0 },
              transition: `opacity ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
            })}
          >
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditDash(d);
                  setEditName(d.name);
                  setEditDesc(d.description || '');
                  setEditOpen(true);
                }}
                sx={(theme) => ({
                  width: 26,
                  height: 26,
                  color: theme.tokens.text.secondary,
                  '&:hover': { color: theme.tokens.text.primary, bgcolor: theme.tokens.surface.sunken },
                })}
              >
                <EditRounded sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            {/* A provisioned report has no delete: removing the row would
                strand its cached rows and leave no way to get it back short
                of re-provisioning. The server refuses it too. */}
            {d.dashboard_type !== 'manual' && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDelDash(d);
                    setDeleteOpen(true);
                  }}
                  sx={(theme) => ({
                    width: 26,
                    height: 26,
                    color: theme.tokens.text.secondary,
                    '&:hover': { color: theme.tokens.semantic.danger, bgcolor: theme.tokens.semantic.dangerSoft },
                  })}
                >
                  <DeleteOutlineRounded sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
          {/* Title + description */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={(theme) => ({
                fontWeight: 600,
                fontSize: '0.9375rem',
                lineHeight: 1.3,
                color: theme.tokens.text.primary,
                letterSpacing: '-0.005em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              })}
            >
              {d.name}
            </Typography>
            {d.description ? (
              <Typography
                sx={(theme) => ({
                  mt: 0.25,
                  fontSize: '0.75rem',
                  lineHeight: 1.45,
                  color: theme.tokens.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word',
                })}
              >
                {d.description}
              </Typography>
            ) : null}
          </Box>

          {/* Footer row: relative freshness + widget count */}
          <Box
            sx={(theme) => ({
              // Pushed to the bottom of the body so the timestamp and badge
              // sit on one line across the row, whether or not the dashboard
              // above them has a description. The rule makes that alignment
              // legible instead of leaving the metadata floating.
              mt: 'auto',
              pt: 1.25,
              borderTop: 1,
              borderColor: theme.tokens.border.subtle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            })}
          >
            <Box sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.6875rem',
              color: theme.tokens.text.tertiary,
              fontWeight: 500,
            })}>
              <AccessTimeIcon sx={{ fontSize: 11 }} />
              {formatRelativeTime(lastTouchedAt) || formatDate(d.created_at)}
            </Box>
            {(widgetCount > 0 || d.dashboard_type === 'manual') && (
              <Box sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.125,
                borderRadius: `${theme.ds.radius.full}px`,
                bgcolor: alpha(accent, 0.10),
                color: alpha(accent, 0.95),
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.005em',
              })}>
                {d.dashboard_type === 'manual'
                  ? 'Report'
                  : `${widgetCount} widget${widgetCount !== 1 ? 's' : ''}`}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };
  

  return (
    <Box>
      {/* Page Header - compact toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, md: 2 },
          mb: 2,
          pb: 1.5,
          borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
          minHeight: { md: 36 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.8125rem',
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            lineHeight: 1.45,
            flex: { md: '0 1 auto' },
            minWidth: 0,
          }}
        >
          Explore insights and KPIs from your dashboards
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            justifyContent: { xs: 'stretch', md: 'flex-end' },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <InputBase
            placeholder="Search dashboards…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={(t) => ({ color: t.tokens.text.tertiary, ml: 0.5, fontSize: 16 })} />
              </InputAdornment>
            }
            sx={(t) => ({
              flex: { xs: '1 1 100%', sm: '1 1 auto' },
              minWidth: { sm: 200 },
              width: { xs: '100%', sm: 240 },
              px: 1.25,
              py: 0.5,
              border: `1px solid ${t.tokens.border.default}`,
              borderRadius: `${t.ds.radius.sm}px`,
              fontSize: '0.8125rem',
              height: 32,
              bgcolor: t.tokens.surface.raised,
              transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, box-shadow ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              '&:hover': { borderColor: t.tokens.border.strong },
              '&:focus-within': {
                borderColor: t.tokens.brand.primary,
                boxShadow: t.tokens.shadow.focusRing,
              },
            })}
          />
          <Box sx={{ display: 'flex', gap: 1, flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: 0 }}>
            <Button
              variant="outlined"
              startIcon={!isMobile ? <SortIcon /> : undefined}
              size="small"
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              sx={(t) => ({
                borderRadius: `${t.ds.radius.sm}px`,
                textTransform: 'none',
                minWidth: isMobile ? 0 : 100,
                flex: isMobile ? 1 : undefined,
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 500,
                borderColor: t.tokens.border.default,
                color: t.tokens.text.secondary,
                '&:hover': { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.sunken },
              })}
            >
              {isMobile ? (sortBy === 'updated' ? 'Sort' : sortBy === 'created' ? 'Created' : 'A–Z') : sortBy === 'updated' ? 'Updated' : sortBy === 'created' ? 'Created' : 'Name'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineRounded />}
              size="small"
              onClick={() => setCreateOpen(true)}
              sx={(t) => ({
                borderRadius: `${t.ds.radius.sm}px`,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 600,
                boxShadow: 'none',
                flex: isMobile ? 1 : undefined,
                '&:hover': { boxShadow: 'none' },
              })}
            >
              {isMobile ? 'New' : 'New dashboard'}
            </Button>
          </Box>
        </Box>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {/* Sort Menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
      >
        <MenuItem 
          selected={sortBy === 'updated'}
          onClick={() => { setSortBy('updated'); setSortMenuAnchor(null); }}
        >
          Last Updated
        </MenuItem>
        <MenuItem 
          selected={sortBy === 'created'}
          onClick={() => { setSortBy('created'); setSortMenuAnchor(null); }}
        >
          Date Created
        </MenuItem>
        <MenuItem 
          selected={sortBy === 'name'}
          onClick={() => { setSortBy('name'); setSortMenuAnchor(null); }}
        >
          Name (A-Z)
        </MenuItem>
      </Menu>
      
      <Box sx={{ mb: 1.5 }}>
        {searchQuery && (
          <Typography sx={(t) => ({ fontSize: '0.8125rem', color: t.tokens.text.tertiary, mb: 1.5 })}>
            {filteredAndSortedDashboards.length} result{filteredAndSortedDashboards.length !== 1 ? 's' : ''} found
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {loading && dashboards.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Paper
              key={i}
              variant="outlined"
              sx={(t) => ({
                borderRadius: `${t.ds.radius.lg}px`,
                borderColor: t.tokens.border.subtle,
                bgcolor: t.tokens.surface.raised,
                overflow: 'hidden',
              })}
            >
              <Box sx={(t) => ({ height: 76, bgcolor: t.tokens.surface.sunken, borderBottom: `1px solid ${t.tokens.border.subtle}` })} />
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={(t) => ({ width: '60%', height: 14, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.xs}px` })} />
                <Box sx={(t) => ({ width: '88%', height: 10, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.xs}px` })} />
                <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={(t) => ({ width: 80, height: 10, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.xs}px` })} />
                  <Box sx={(t) => ({ width: 56, height: 14, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.full}px` })} />
                </Box>
              </Box>
            </Paper>
          ))
        ) : filteredAndSortedDashboards.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Paper
              variant="outlined"
              sx={(t) => ({
                p: 5,
                textAlign: 'center',
                bgcolor: t.tokens.surface.sunken,
                borderRadius: `${t.ds.radius.lg}px`,
                borderStyle: 'dashed',
                borderColor: t.tokens.border.default,
              })}
            >
              <Box sx={(t) => ({
                width: 44, height: 44, mx: 'auto', mb: 1.5,
                borderRadius: `${t.ds.radius.md}px`,
                bgcolor: t.tokens.brand.primarySoft,
                color: t.tokens.brand.primary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              })}>
                <DashboardIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography sx={(t) => ({ mb: 0.5, fontWeight: 600, fontSize: '0.9375rem', color: t.tokens.text.primary })}>
                {searchQuery ? 'No dashboards found' : 'No dashboards yet'}
              </Typography>
              <Typography sx={(t) => ({ mb: searchQuery ? 0 : 2.5, fontSize: '0.8125rem', color: t.tokens.text.secondary })}>
                {searchQuery ? 'Try adjusting your search query' : 'Create your first dashboard to visualize your data'}
              </Typography>
              {!searchQuery && (
                <Button variant="contained" startIcon={<AddCircleOutlineRounded />} onClick={() => setCreateOpen(true)}>
                  Create dashboard
                </Button>
              )}
            </Paper>
          </Box>
        ) : (
          filteredAndSortedDashboards.map((d, idx) => renderDashboardCard(d, idx))
        )}
      </Box>
      

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        size="sm"
        title="New dashboard"
        busy={creating}
        primaryAction={{ label: 'Create', onClick: handleCreate }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setCreateOpen(false) }}
      >
        <TextField autoFocus label="Name" fullWidth margin="normal" value={createName} onChange={(e) => setCreateName(e.target.value)} />
        <TextField label="Description" fullWidth margin="normal" value={createDesc} onChange={(e) => setCreateDesc(e.target.value)} />
        {createErr && <Alert severity="error" sx={{ mt: 1 }}>{createErr}</Alert>}
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        size="sm"
        title="Edit dashboard"
        busy={saving}
        primaryAction={{ label: 'Save', onClick: handleSave }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setEditOpen(false) }}
      >
        <TextField autoFocus label="Name" fullWidth margin="normal" value={editName} onChange={(e) => setEditName(e.target.value)} />
        <TextField label="Description" fullWidth margin="normal" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
        {editErr && <Alert severity="error" sx={{ mt: 1 }}>{editErr}</Alert>}
      </AppDialog>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete dashboard"
        description={<>Are you sure you want to delete <strong>{delDash?.name}</strong>?</>}
        body={<Alert severity="warning">This action cannot be undone.</Alert>}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        tone="destructive"
        busy={deleting}
      />
    </Box>
  );
}
